import type { TmsWriteRow } from '@numeo/types-main-web-app'
import type { GeneralChatMessage } from '../../types'
import type { ChatCard } from '../results/cardResults'
import { loadKeyOf } from '../results/loadKey'
import type { AssignTarget } from './assignStore'

/** The tool whose writes (assign, unassign) the chat stores, direct or in a turn. */
const ASSIGN_TOOL = 'tms_loads.assign'

/** A load's truck as its last stored write left it, at the time of that write. A null target is a load taken off its truck. */
export interface StoredAssign {
    at: number
    target: AssignTarget | null
}

/** The stored assigns of a tab, by load key. */
export type StoredAssigns = Record<string, StoredAssign>

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

/** The write rows of a stored tool output (`{ text, structured: { rows } }`); an output that does not parse holds none. */
function writeRowsOf(output: string | null | undefined): TmsWriteRow[] {
    if (!output) return []
    try {
        const parsed: unknown = JSON.parse(output)
        if (!isRecord(parsed) || !isRecord(parsed.structured) || !Array.isArray(parsed.structured.rows)) return []
        return parsed.structured.rows.filter((row): row is TmsWriteRow => isRecord(row) && typeof row.outcome === 'string')
    } catch {
        return []
    }
}

/** The truck a written row puts the load on: its label as the card names it, with its drivers; null when the row names no truck. */
function targetOf(row: TmsWriteRow): AssignTarget | null {
    const truck = typeof row.truck === 'string' ? row.truck.replace(/^truck\s+/i, '').trim() : ''
    if (!truck) return null
    return { truckId: '', truckLabel: truck, drivers: (row.drivers ?? []).filter((name) => typeof name === 'string') }
}

/** Adds the updated rows of one write, made at `at`, to `out`; a later write of a load already in `out` stays. */
function addWriteRows(out: StoredAssigns, rows: TmsWriteRow[], at: number): void {
    for (const row of rows) {
        const key = loadKeyOf(row)
        if (key === undefined || row.outcome !== 'updated') continue
        if (out[key] && out[key].at > at) continue
        out[key] = { at, target: targetOf(row) }
    }
}

/**
 * Each load's newest stored direct write in a tab's rows, by the key the chat's cards use. A row that is not an assign write, or
 * whose output does not parse, is skipped; a later write of a load replaces an earlier one.
 */
export function storedAssignsOf(rows: GeneralChatMessage[]): StoredAssigns {
    const out: StoredAssigns = {}
    for (const message of rows) {
        if (message.message_type !== 'agent-tool-result' || message.tool_name !== ASSIGN_TOOL) continue
        const at = Date.parse(message.created_at)
        if (Number.isNaN(at)) continue
        addWriteRows(out, writeRowsOf(message.tool_output), at)
    }
    return out
}

/** The assigns the chat's own turns wrote (the agent's assign or unassign), from the write cards the chat already draws. */
export function chatAssignsOf(cards: ChatCard[]): StoredAssigns {
    const out: StoredAssigns = {}
    for (const { createdAt, view } of cards) {
        if (view.view !== 'writes' || view.card !== ASSIGN_TOOL) continue
        const at = Date.parse(createdAt)
        if (Number.isNaN(at)) continue
        addWriteRows(out, view.rows, at)
    }
    return out
}

/** Each load's newer entry of the two sources: a write made later wins, whichever source it came from. */
export function newestAssigns(a: StoredAssigns, b: StoredAssigns): StoredAssigns {
    const merged = { ...a }
    for (const [key, entry] of Object.entries(b)) if (!merged[key] || merged[key].at < entry.at) merged[key] = entry
    return merged
}

/** Leaves out a stored write that a newer loads card already shows: that card is the load's state as of its own read. */
export function storedAssignsNewerThan(assigns: StoredAssigns, cards: ChatCard[]): StoredAssigns {
    const readAt: Record<string, number> = {}
    for (const { createdAt, view } of cards) {
        if (view.view !== 'loads') continue
        const at = Date.parse(createdAt)
        view.rows.forEach((row) => {
            const key = loadKeyOf(row)
            if (key !== undefined) readAt[key] = Math.max(readAt[key] ?? Number.NEGATIVE_INFINITY, at)
        })
    }
    return Object.fromEntries(Object.entries(assigns).filter(([key, stored]) => !(readAt[key] > stored.at)))
}
